import React, { useEffect, useState } from 'react';

// Maakt de Huismeester-pagina (/schilder) installeerbaar als "app" op de telefoon.
// 1) Wisselt de site-manifest om naar de toegewijde Huismeester-manifest (start_url /schilder,
//    standalone, eigen icoon) zolang dit component gemount is — de rest van de site blijft ongemoeid.
// 2) Zet de iOS-metatags zodat "Zet op beginscherm" in Safari een echte fullscreen-app geeft.
// 3) Toont een installatie-banner: een knop op Android/Chrome (beforeinstallprompt) en
//    korte uitleg op iPhone (Safari heeft geen prompt-API).

const MANIFEST_HREF = '/manifest-huismeester.json';
const APPLE_ICON = '/huismeester-icon-192.png';
const THEME_COLOR = '#246834';
const DISMISS_KEY = 'huismeesterInstallDismissed';

// Zet (of vervang) een <meta>/<link> in de <head> en geef een herstel-functie terug.
function setHeadTag(selector, create) {
  const existing = document.head.querySelector(selector);
  const prev = existing ? existing.cloneNode(true) : null;
  const el = existing || create();
  if (!existing) document.head.appendChild(el);
  return { el, restore: () => {
    if (prev) el.replaceWith(prev);
    else el.remove();
  } };
}

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export default function HuismeesterInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [showHelp, setShowHelp] = useState(false);

  // Head-tags omzetten naar de Huismeester-app zolang we op deze pagina zijn.
  // setHeadTag bewaart de originele staat (kloon of "bestond niet") en geeft een
  // restore() die de <head> exact terugzet bij unmount.
  useEffect(() => {
    const tags = [
      // Manifest omwisselen naar de toegewijde Huismeester-manifest.
      ['link[rel="manifest"]', 'link', { rel: 'manifest', href: MANIFEST_HREF }],
      // Theme-color: statusbalk-kleur in standalone.
      ['meta[name="theme-color"]', 'meta', { name: 'theme-color', content: THEME_COLOR }],
      // iOS: fullscreen-app + titel + icoon ("Zet op beginscherm").
      ['meta[name="apple-mobile-web-app-capable"]', 'meta', { name: 'apple-mobile-web-app-capable', content: 'yes' }],
      ['meta[name="apple-mobile-web-app-status-bar-style"]', 'meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' }],
      ['meta[name="apple-mobile-web-app-title"]', 'meta', { name: 'apple-mobile-web-app-title', content: 'Huismeester' }],
      ['link[rel="apple-touch-icon"]', 'link', { rel: 'apple-touch-icon', href: APPLE_ICON }],
    ];
    const restores = [];
    for (const [selector, tag, attrs] of tags) {
      const t = setHeadTag(selector, () => document.createElement(tag));
      for (const [k, v] of Object.entries(attrs)) t.el.setAttribute(k, v);
      restores.push(t.restore);
    }
    return () => restores.reverse().forEach((fn) => fn());
  }, []);

  // Service worker registreren (scope beperkt tot /schilder) — nodig voor een
  // echte installeerbare app op Android. Faalt stil als SW niet beschikbaar is.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/huismeester-sw.js', { scope: '/schilder' })
      .catch(() => {});
  }, []);

  // Android/Chrome: vang de install-prompt op.
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setDeferredPrompt(null); setDismissed(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Alleen verbergen als de app al als losstaande app draait, of als hij is weggeklikt.
  if (isStandalone() || dismissed) return null;

  const dismiss = () => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      // Geen automatische prompt (iOS, of nog niet afgegeven): toon de handmatige uitleg.
      setShowHelp((v) => !v);
    }
  };

  return (
    <div className="hp-install">
      <div className="hp-install-row">
        <span className="hp-install-ico" aria-hidden>📲</span>
        <span className="hp-install-txt">Zet deze pagina als app op je telefoon</span>
        <button className="hp-install-btn" onClick={install}>
          {deferredPrompt ? 'Installeer' : 'Hoe?'}
        </button>
        <button className="hp-install-x" onClick={dismiss} aria-label="Sluiten">✕</button>
      </div>
      {showHelp && !deferredPrompt && (
        <p className="hp-install-help">
          {isIos() ? (
            <>Tik onderin op <strong>Delen</strong> <span aria-hidden>⬆️</span> en kies{' '}
            <strong>‘Zet op beginscherm’</strong>.</>
          ) : (
            <>Open het <strong>browsermenu</strong> <span aria-hidden>⋮</span> en kies{' '}
            <strong>‘App installeren’</strong> of <strong>‘Toevoegen aan startscherm’</strong>.</>
          )}
        </p>
      )}
    </div>
  );
}
